import { plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { useInstrumentTimeSeriesData } from 'features/knowledgeDocumentViewer/hooks/useInstrumentTimeSeriesData'
import { dedupeById } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import type { MatchedDatapoint } from 'features/timeSeries'
import type { ReactElement } from 'react'

import { SparklineTooltipContent } from './SparklineTooltipContent'

const TOOLTIP_SERIES_BOUND = 8

export function InstrumentTimeSeriesPreview({
  matchedDatapoints,
  linkedDocumentCount = 0,
  clickHint,
}: {
  matchedDatapoints: MatchedDatapoint[]
  linkedDocumentCount?: number
  clickHint?: string | null
}): ReactElement {
  const { t } = useLingui()
  const resolvedClickHint = clickHint === undefined ? t`dashboard` : clickHint
  // Dedupe by id only: callers already text-dedupe where appropriate, and
  // fleet-template links are distinct streams under one shared name — a
  // name-collapse here would erase 11 of 12 gensets.
  const dedupedDatapoints = dedupeById(matchedDatapoints)
  const datapointIdToDescription = new Map(
    dedupedDatapoints.flatMap(({ id, description }): [string, string][] =>
      description.trim() ? [[id, description.trim()]] : []
    )
  )
  const datapointIds = dedupedDatapoints
    .slice(0, TOOLTIP_SERIES_BOUND)
    .map((dp) => dp.id)

  const { sparklineData, isLoading, error } = useInstrumentTimeSeriesData(
    datapointIds,
    datapointIds.length > 0,
    { maxIds: TOOLTIP_SERIES_BOUND }
  )

  const hasTimeSeriesData =
    !isLoading &&
    !error &&
    sparklineData.length > 0 &&
    dedupedDatapoints.length > 0
  const hasLinkedDocuments = linkedDocumentCount > 0
  const hasClickHint = hasTimeSeriesData && Boolean(resolvedClickHint)

  const actionsHint =
    hasClickHint || hasLinkedDocuments ? (
      <>
        {hasClickHint && t`Click: ${resolvedClickHint}`}
        {hasClickHint && hasLinkedDocuments && ' · '}
        {hasLinkedDocuments &&
          t`Shift+click: ${plural(linkedDocumentCount, { one: '# doc', other: '# docs' })}`}
      </>
    ) : undefined

  return (
    <SparklineTooltipContent
      sparklineData={sparklineData}
      isLoading={isLoading}
      error={!!error}
      datapointCount={dedupedDatapoints.length}
      maxSeries={TOOLTIP_SERIES_BOUND}
      animateLoading
      actionsHint={actionsHint}
      carousel
      datapointIdToDescription={datapointIdToDescription}
    />
  )
}
